import React, { useCallback, useEffect, useRef, useState } from "react";
import { useAppContext } from "../../contexts/AppContext";
import {
  getUserNotificationsPage,
  markNotificationSeen,
} from "../../api/notifications";
import InAppNotificationModal from "./InAppNotificationModal";
import {
  isInAppModalNotification,
  nextUnreadInAppModalNotification,
} from "./inAppModalUtils";

const REFRESH_INTERVAL_MS = 30_000;

const InAppNotificationSurface = ({
  blocked = false,
  onActiveChange = null,
  sidebarOffset = 0,
}) => {
  const { appProps } = useAppContext();
  const loadingUserData = appProps?.loadingUserData;
  const userId = appProps?.userData?.user_id;
  const [activeNotification, setActiveNotification] = useState(null);
  const surfacedNotificationIdsRef = useRef(new Set());

  useEffect(() => {
    onActiveChange?.(Boolean(activeNotification));
  }, [activeNotification, onActiveChange]);

  const surfaceNotification = useCallback(
    async (notification) => {
      if (!notification?.id || notification?.seenAt) return false;
      if (!isInAppModalNotification(notification)) return false;
      if (blocked || activeNotification) return false;

      const notificationId = String(notification.id);
      if (surfacedNotificationIdsRef.current.has(notificationId)) return false;

      surfacedNotificationIdsRef.current.add(notificationId);
      setActiveNotification(notification);

      try {
        await markNotificationSeen(notification.id);
        const seenAt = new Date().toISOString();
        window.dispatchEvent(
          new CustomEvent("user-notifications:updated", {
            detail: {
              notification: {
                ...notification,
                seenAt,
                readAt: notification?.readAt || seenAt,
              },
            },
          }),
        );
      } catch (error) {
        console.error("mark in-app notification seen failed", error);
      }

      return true;
    },
    [activeNotification, blocked],
  );

  const refreshUnreadNotifications = useCallback(async () => {
    if (loadingUserData || !userId || blocked || activeNotification) return;

    const page = await getUserNotificationsPage({
      limit: 20,
      offset: 0,
      unreadOnly: true,
    });
    const nextNotification = nextUnreadInAppModalNotification(
      page,
      surfacedNotificationIdsRef.current,
    );
    if (nextNotification) await surfaceNotification(nextNotification);
  }, [
    activeNotification,
    blocked,
    loadingUserData,
    surfaceNotification,
    userId,
  ]);

  useEffect(() => {
    surfacedNotificationIdsRef.current = new Set();
    setActiveNotification(null);
  }, [userId]);

  useEffect(() => {
    if (loadingUserData || !userId) return undefined;

    refreshUnreadNotifications();
    const intervalId = window.setInterval(
      refreshUnreadNotifications,
      REFRESH_INTERVAL_MS,
    );
    return () => window.clearInterval(intervalId);
  }, [loadingUserData, refreshUnreadNotifications, userId]);

  useEffect(() => {
    if (blocked || activeNotification) return;
    refreshUnreadNotifications();
  }, [activeNotification, blocked, refreshUnreadNotifications]);

  useEffect(() => {
    const handleNotificationUpdate = (event) => {
      const notification = event?.detail?.notification;
      if (!notification) return;
      surfaceNotification(notification);
    };

    window.addEventListener(
      "user-notifications:updated",
      handleNotificationUpdate,
    );
    return () =>
      window.removeEventListener(
        "user-notifications:updated",
        handleNotificationUpdate,
      );
  }, [surfaceNotification]);

  if (!activeNotification || blocked) return null;

  return (
    <InAppNotificationModal
      notification={activeNotification}
      onClose={() => setActiveNotification(null)}
      sidebarOffset={sidebarOffset}
    />
  );
};

export default InAppNotificationSurface;
